import { useQueries } from '@tanstack/react-query';
import { lazyComponent } from '@/app/lazyWithPreload';
import { useApi } from '@/api/provider';
import { queryKeys } from '@/api/queryKeys';
import { STALE_TIMES } from '@/api/queryClient';

/**
 * The Do not disturb indicator's code is fetched only while it has something
 * to say (#197), like `InstallPromptGate`: most sessions have no snooze
 * running, and the entry chunk has no room to spare. The gate reads the same
 * preferences cache the shell's pollers fill (observers that never fetch) and
 * asks one cheap question: does any mailbox's answer carry a snooze that was
 * still running when it arrived? The slot does the real work (which mailbox,
 * how long, when it ends). Prefetched at idle (`prefetch.ts`).
 */
export const DndSlot = lazyComponent(() => import('@/features/dnd/DndSlot'), 'DndSlot');

function snoozeOpenAt(until: string | undefined, at: number): boolean {
  return until !== undefined && Date.parse(until) > at;
}

export function DndIndicatorGate({
  activeAccountId,
  accountIds,
  variant,
}: {
  activeAccountId: string | undefined;
  accountIds: readonly string[];
  variant: 'topbar' | 'drawer';
}) {
  const api = useApi();
  const results = useQueries({
    queries: accountIds.map((id) => ({
      queryKey: queryKeys.account(id).notificationPrefs(),
      queryFn: ({ signal }: { signal: AbortSignal }) => api.push.getPreferences(id, signal),
      staleTime: STALE_TIMES.metadata,
      enabled: false,
    })),
  });
  const any = results.some(
    (result) =>
      snoozeOpenAt(result.data?.snoozedUntil, result.dataUpdatedAt) ||
      snoozeOpenAt(result.data?.ownerSnoozedUntil, result.dataUpdatedAt),
  );
  if (activeAccountId === undefined || !any) return null;
  return <DndSlot activeAccountId={activeAccountId} accountIds={accountIds} variant={variant} />;
}
